import { ChangeEvent, FormEvent, useMemo, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Download,
  FileUp,
  KeyRound,
  Share2,
} from "lucide-react";
import { saveRoutePayload, fetchRoutePayload } from "./api";
import { createReadingsCsvFile } from "./share";
import { createRouteFingerprint, decryptJson, encryptJson } from "./crypto";
import { downloadExcelTemplate, parseRouteWorkbook } from "./excel";
import {
  clearDraftPayload,
  loadDraftPayload,
  saveDraftPayload,
} from "./storage";
import type { DraftState, RouteCustomer, RouteList } from "./types";

type Mode = "operator" | "admin";
type Phase = "unlock" | "date" | "reading" | "complete";

const today = () => {
  const now = new Date();
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
  return now.toISOString().slice(0, 10);
};

const createEmptyDraft = (
  routeFingerprint: string,
  readingDate = today(),
): DraftState => ({
  routeFingerprint,
  readingDate,
  currentIndex: 0,
  readings: {},
  completed: false,
});

export const App = () => {
  const [mode, setMode] = useState<Mode>("operator");
  const [phase, setPhase] = useState<Phase>("unlock");
  const [passphrase, setPassphrase] = useState("");
  const [route, setRoute] = useState<RouteList | null>(null);
  const [routeFingerprint, setRouteFingerprint] = useState("");
  const [draft, setDraft] = useState<DraftState | null>(null);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [csvFilename, setCsvFilename] = useState("");

  const currentCustomer = useMemo<RouteCustomer | null>(() => {
    if (!route || !draft) return null;
    return route.customers[draft.currentIndex] ?? null;
  }, [draft, route]);

  const progressText =
    route && draft
      ? `${draft.currentIndex + 1} / ${route.customers.length}`
      : "0 / 0";

  const persistDraft = async (nextDraft: DraftState) => {
    setDraft(nextDraft);
    const encrypted = await encryptJson(nextDraft, passphrase);
    await saveDraftPayload(encrypted);
  };

  const resetWorkspace = () => {
    setRoute(null);
    setRouteFingerprint("");
    setDraft(null);
    setPhase("unlock");
    setStatus("");
    setError("");
    if (shareUrl) {
      URL.revokeObjectURL(shareUrl);
      setShareUrl("");
    }
  };

  const handleUnlock = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError("");
    setStatus("");

    try {
      const encryptedRoute = await fetchRoutePayload();
      if (!encryptedRoute) {
        setError("巡回リストがまだ登録されていません。");
        return;
      }

      const routeList = await decryptJson<RouteList>(encryptedRoute, passphrase);
      const fingerprint = await createRouteFingerprint(routeList.customers);
      let nextDraft = createEmptyDraft(fingerprint);
      let restored = false;

      const encryptedDraft = await loadDraftPayload();
      if (encryptedDraft) {
        try {
          const restoredDraft = await decryptJson<DraftState>(
            encryptedDraft,
            passphrase,
          );
          if (restoredDraft.routeFingerprint === fingerprint) {
            nextDraft = restoredDraft;
            restored = true;
          }
        } catch {
          await clearDraftPayload();
        }
      }

      setRoute(routeList);
      setRouteFingerprint(fingerprint);
      setDraft(nextDraft);
      setPhase(restored ? (nextDraft.completed ? "complete" : "reading") : "date");
      setStatus(`巡回リスト ${routeList.customers.length}件を読み込みました。`);
    } catch {
      setError(
        "巡回リストを開けませんでした。\nパスワードが登録時と同じか確認してください。\n登録直後の確認でこの表示が出る場合は、管理画面でExcelをもう一度取り込んでから巡回を開いてください。",
      );
    } finally {
      setBusy(false);
    }
  };

  const handleStartDate = async (event: FormEvent) => {
    event.preventDefault();
    if (!draft) return;
    await persistDraft({
      ...draft,
      readingDate: draft.readingDate || today(),
      completed: false,
    });
    setPhase("reading");
  };

  const handleReadingChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!draft || !currentCustomer) return;
    const readings = {
      ...draft.readings,
      [currentCustomer.id]: event.target.value,
    };
    setDraft({ ...draft, readings });
  };

  const saveCurrentAndMove = async (direction: "back" | "next" | "complete") => {
    if (!draft || !route) return;

    const maxIndex = route.customers.length - 1;
    const nextIndex =
      direction === "back"
        ? Math.max(0, draft.currentIndex - 1)
        : Math.min(maxIndex, draft.currentIndex + 1);

    const nextDraft: DraftState = {
      ...draft,
      currentIndex: direction === "complete" ? draft.currentIndex : nextIndex,
      completed: direction === "complete",
    };

    await persistDraft(nextDraft);
    if (direction === "complete") {
      setPhase("complete");
      await shareCsv(nextDraft);
    }
  };

  const shareCsv = async (sourceDraft = draft) => {
    if (!route || !sourceDraft) return;

    if (shareUrl) {
      URL.revokeObjectURL(shareUrl);
      setShareUrl("");
    }

    const file = createReadingsCsvFile(
      sourceDraft.readingDate,
      route.customers,
      sourceDraft.readings,
    );
    setCsvFilename(file.name);

    const canShareFile =
      "canShare" in navigator &&
      navigator.canShare?.({
        files: [file],
      });

    if (canShareFile && navigator.share) {
      try {
        await navigator.share({
          title: "検針CSV",
          text: "検針結果CSV",
          files: [file],
        });
        setStatus("CSVを共有しました。");
        return;
      } catch (shareError) {
        if ((shareError as DOMException).name === "AbortError") {
          setStatus("共有をキャンセルしました。");
        }
      }
    }

    const url = URL.createObjectURL(file);
    setShareUrl(url);
    setStatus("CSVをダウンロードできます。");
  };

  const handleAdminUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!passphrase) {
      setError("先にパスワードを入力してください。");
      return;
    }

    setBusy(true);
    setError("");
    setStatus("");

    try {
      const parsed = await parseRouteWorkbook(file);
      if (!parsed.ok) {
        setError(parsed.errors.join("\n"));
        return;
      }

      const encrypted = await encryptJson(parsed.routeList, passphrase);
      const result = await saveRoutePayload(encrypted);
      const saveText =
        result.ok && result.location === "server"
          ? `${parsed.routeList.customers.length}件を登録しました。\n巡回者のスマホからも使える状態です。\n巡回者はスマホで同じURLを開き、同じパスワードを入力してください。`
          : `${parsed.routeList.customers.length}件を読み込みましたが、まだ巡回者のスマホからは使えません。\n今の画面で確認用に読み込めただけです。\n管理者は、共有保存の設定が終わった本番URLで、もう一度このExcelを取り込んでください。`;

      setStatus(saveText);
    } catch {
      setError("Excelの取込または保存に失敗しました。");
    } finally {
      setBusy(false);
      event.target.value = "";
    }
  };

  const startNewRound = async () => {
    if (!route || !routeFingerprint) return;
    const nextDraft = createEmptyDraft(routeFingerprint);
    await persistDraft(nextDraft);
    setPhase("date");
  };

  const readingValue =
    draft && currentCustomer ? draft.readings[currentCustomer.id] ?? "" : "";
  const isLast =
    route && draft ? draft.currentIndex === route.customers.length - 1 : false;

  return (
    <main className="app-shell">
      <header className="app-header">
        <div>
          <p className="eyebrow">紫波中央駅前エネルギーステーション</p>
          <h1>検針入力</h1>
        </div>
        <nav className="mode-switch" aria-label="画面切替">
          <button
            className={mode === "operator" ? "active" : ""}
            onClick={() => {
              setMode("operator");
              resetWorkspace();
            }}
            type="button"
          >
            巡回
          </button>
          <button
            className={mode === "admin" ? "active" : ""}
            onClick={() => {
              setMode("admin");
              resetWorkspace();
            }}
            type="button"
          >
            管理
          </button>
        </nav>
      </header>

      {mode === "admin" ? (
        <section className="panel">
          <div className="section-title">
            <FileUp aria-hidden="true" />
            <h2>巡回リスト登録</h2>
          </div>
          <p className="help-text">
            管理者がパソコンで顧客リストを登録する画面です。
          </p>
          <ol className="admin-steps">
            <li>まず、下のパスワード欄に任意のパスワードを入力します。</li>
            <li>次に、Excel取込から顧客リストのExcelファイルを選びます。</li>
            <li>Excelの列名は、1行目に「順番」「顧客番号」「氏名」と入れてください。</li>
            <li>形式が分からない場合は、テンプレートをダウンロードして使えます。</li>
          </ol>

          <label className="field">
            <span>パスワード</span>
            <input
              value={passphrase}
              onChange={(event) => setPassphrase(event.target.value)}
              type="password"
              autoComplete="current-password"
            />
          </label>

          <div className="actions two-columns">
            <button className="secondary" onClick={downloadExcelTemplate} type="button">
              <Download aria-hidden="true" />
              テンプレート
            </button>
            <label className="file-button">
              <FileUp aria-hidden="true" />
              Excel取込
              <input
                accept=".xlsx,.xls"
                disabled={busy}
                onChange={handleAdminUpload}
                type="file"
              />
            </label>
          </div>
        </section>
      ) : (
        <>
          {phase === "unlock" && (
            <section className="panel">
              <div className="section-title">
                <KeyRound aria-hidden="true" />
                <h2>巡回開始</h2>
              </div>
              <form onSubmit={handleUnlock}>
                <label className="field">
                  <span>パスワード</span>
                  <input
                    value={passphrase}
                    onChange={(event) => setPassphrase(event.target.value)}
                    required
                    type="password"
                    autoComplete="current-password"
                  />
                </label>
                <button className="primary full" disabled={busy} type="submit">
                  読み込む
                  <ArrowRight aria-hidden="true" />
                </button>
              </form>
            </section>
          )}

          {phase === "date" && draft && (
            <section className="panel">
              <h2>検針日</h2>
              <form onSubmit={handleStartDate}>
                <label className="field">
                  <span>日付</span>
                  <input
                    value={draft.readingDate || today()}
                    onChange={(event) =>
                      setDraft({ ...draft, readingDate: event.target.value })
                    }
                    required
                    type="date"
                  />
                </label>
                <button className="primary full" type="submit">
                  次へ
                  <ArrowRight aria-hidden="true" />
                </button>
              </form>
            </section>
          )}

          {phase === "reading" && route && draft && currentCustomer && (
            <section className="reader">
              <div className="progress-row">
                <span>{progressText}</span>
                <button className="link-button" onClick={startNewRound} type="button">
                  最初から
                </button>
              </div>
              <div className="meter-panel">
                <dl className="customer-data">
                  <div>
                    <dt>順番</dt>
                    <dd>{currentCustomer.order}</dd>
                  </div>
                  <div>
                    <dt>顧客番号</dt>
                    <dd>{currentCustomer.customerNumber}</dd>
                  </div>
                  <div>
                    <dt>氏名</dt>
                    <dd>{currentCustomer.name}</dd>
                  </div>
                </dl>

                <label className="reading-input">
                  <span>検針結果</span>
                  <input
                    value={readingValue}
                    onChange={handleReadingChange}
                    inputMode="decimal"
                    type="number"
                    placeholder="0"
                  />
                </label>
              </div>

              <div className="actions navigation">
                <button
                  className="secondary"
                  disabled={draft.currentIndex === 0}
                  onClick={() => saveCurrentAndMove("back")}
                  type="button"
                >
                  <ArrowLeft aria-hidden="true" />
                  戻る
                </button>
                <button
                  className="primary"
                  onClick={() => saveCurrentAndMove(isLast ? "complete" : "next")}
                  type="button"
                >
                  {isLast ? (
                    <>
                      完了
                      <Check aria-hidden="true" />
                    </>
                  ) : (
                    <>
                      次へ
                      <ArrowRight aria-hidden="true" />
                    </>
                  )}
                </button>
              </div>
            </section>
          )}

          {phase === "complete" && draft && route && (
            <section className="panel success">
              <div className="section-title">
                <Check aria-hidden="true" />
                <h2>完了</h2>
              </div>
              <p className="summary-line">
                {draft.readingDate} / {route.customers.length}件
              </p>
              <div className="actions">
                <button className="primary full" onClick={() => shareCsv()} type="button">
                  <Share2 aria-hidden="true" />
                  CSV共有
                </button>
                {shareUrl && (
                  <a className="download-link" download={csvFilename} href={shareUrl}>
                    <Download aria-hidden="true" />
                    CSVダウンロード
                  </a>
                )}
                <button className="secondary full" onClick={startNewRound} type="button">
                  新しい検針
                </button>
              </div>
            </section>
          )}
        </>
      )}

      {(status || error) && (
        <aside className={error ? "notice error" : "notice"} aria-live="polite">
          {error || status}
        </aside>
      )}
    </main>
  );
};
